import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { getPaymentStatus, processPaymentSuccess } from '../services/paymentService';

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState(null);
  const [processingData, setProcessingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const paymentStatus = searchParams.get('payment');
  const sessionId = searchParams.get('session_id');
  const credits = searchParams.get('credits');
  const amount = searchParams.get('amount');
  const packageId = searchParams.get('package_id');

  useEffect(() => {
    if (sessionId && paymentStatus === 'success') {
      fetchPaymentStatus();
      processPayment();
    }
  }, [sessionId, paymentStatus]);

  const fetchPaymentStatus = async () => {
    setLoading(true);
    try {
      const response = await getPaymentStatus(sessionId);
      setPaymentData(response.data);
    } catch (error) {
      setError('Failed to fetch payment status');
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    setProcessing(true);
    try {
      const paymentData = {
        sessionId,
        credits: parseInt(credits),
        amount: parseFloat(amount),
        packageId: parseInt(packageId)
      };
      
      
      const response = await processPaymentSuccess(paymentData);
      // setProcessingData(response.data);
      if (response.status === 'success') {
        localStorage.setItem('userInfo', JSON.stringify(response.data));
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('❌ Error processing payment:', error);
      setError('Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />;
      case 'canceled':
        return <CancelIcon sx={{ fontSize: 60, color: '#f44336' }} />;
      default:
        return <PaymentIcon sx={{ fontSize: 60, color: '#2196f3' }} />;
    }
  };

  const getStatusColor = () => {
    switch (paymentStatus) {
      case 'success':
        return '#4caf50';
      case 'canceled':
        return '#f44336';
      default:
        return '#2196f3';
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return 'Payment Successful!';
      case 'canceled':
        return 'Payment Canceled';
      default:
        return 'Payment Status Unknown';
    }
  };

  if (!paymentStatus) {
    return null;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh',
      p: 3
    }}>
      <Card sx={{ maxWidth: 600, width: '100%', textAlign: 'center' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Status Icon */}
          <Box sx={{ mb: 3 }}>
            {getStatusIcon()}
          </Box>

          {/* Status Title */}
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: getStatusColor(),
            mb: 2 
          }}>
            {getStatusMessage()}
          </Typography>

          {/* Payment Details */}
          {paymentStatus === 'success' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                <Chip 
                  icon={<CreditCardIcon />}
                  label={`${credits} Credits`}
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body1">
                  Amount: ${amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Package ID: {packageId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Session ID: {sessionId}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Payment Status Data */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {processing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Processing payment and updating credits...
                </Typography>
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {paymentData && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Status
              </Typography>
              <Box sx={{ 
                bgcolor: '#f5f5f5', 
                p: 2, 
                borderRadius: 1,
                textAlign: 'left'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Status:</strong> {paymentData.status}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Payment Status:</strong> {paymentData.paymentStatus}
                </Typography>
                <Typography variant="body2">
                  <strong>Session ID:</strong> {paymentData.sessionId}
                </Typography>
              </Box>
            </Box>
          )}

          {processingData && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Processing Results
              </Typography>
              <Box sx={{ 
                bgcolor: '#e8f5e8', 
                p: 2, 
                borderRadius: 1,
                textAlign: 'left'
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Order ID:</strong> {processingData.orderId}
                </Typography>
                {processingData.subscriptionId && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Subscription ID:</strong> {processingData.subscriptionId}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Credits Added:</strong> {processingData.creditsAdded}
                </Typography>
                {processingData.creditsTransferred && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Credits Transferred:</strong> {processingData.creditsTransferred}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total Credits:</strong> {processingData.totalCredits}
                </Typography>
                <Typography variant="body2">
                  <strong>Order Status:</strong> {processingData.orderStatus ? 'Completed' : 'Pending'}
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              href="/dashboard"
              sx={{ 
                bgcolor: '#1ce484',
                '&:hover': { bgcolor: '#20ec84' }
              }}
            >
              Back to Dashboard
            </Button>
            {paymentStatus === 'success' && (
              <Button 
                variant="outlined" 
                onClick={fetchPaymentStatus}
                disabled={loading}
              >
                Refresh Status
              </Button>
            )}
          </Box>

          {/* Debug Information */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f0f0f0', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Debug Info:
              </Typography>
              <Typography variant="caption" display="block">
                Payment Status: {paymentStatus}
              </Typography>
              <Typography variant="caption" display="block">
                Session ID: {sessionId}
              </Typography>
              <Typography variant="caption" display="block">
                Credits: {credits}
              </Typography>
              <Typography variant="caption" display="block">
                Amount: ${amount}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentStatus; 